import { faLocationArrow, faMap } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DialogContentText } from "@mui/material";
import { notification } from "antd";
import React, { useEffect, useMemo } from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { CalendarEvent } from "../../../models/CalendarEvent";
import { CalendarEventResource } from "../../../models/CalendarEventResource";
import { Dropoff } from "../../../models/Dropoff";
import { Gate } from "../../../models/Gate";
import { HaulRoute } from "../../../models/HaulRoute";
import { MapObject } from "../../../models/MapObject";
import PlotAPI from "../../../plotAPI";
import { gateToDropoffRoutesForPair } from "../../Helper/Map";
import AreaAvatar from "../../Map/AreaAvatar";
import DamnMap from "../damnMap";
import DamnDirectionsModal from "./damnDirectionsModal";
import DropoffSection from "./dropoffSection";
import GateSection from "./gateSection";
import GateToDropoffRouteSection from "./gateToDropoffRouteSection";
import HaulRouteSection from "./haulRouteSection";
import { ALTERNATE_HAUL_ROUTE_COLOR } from "./haulRouteSelector";

interface DamnMapSectionProps {
    calendarEvent: CalendarEvent;
    setCalendarEvent: (calendarEvent: CalendarEvent) => void;
}

const DamnMapSection: React.FC<DamnMapSectionProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const {
        dropoffsByProject,
        dropoffsForProject,
        mapObjectsByProject,
        haulRoutesForProject,
        haulRoutesByProject,
        gatesForProject,
        gateToDropoffRoutesForProject,
        gateToDropoffRoutesByProject,
        projectInfo,
    } = context;

    const project = projectInfo(props.calendarEvent.projectId);

    const [dropoff, setDropoff] = React.useState<Dropoff | null>(null);
    const [damnDirectionsModal, setDamnDirectionsModal] = React.useState<boolean>(false);
    const [savingHaulRoute, setSavingHaulRoute] = React.useState<boolean>(false);
    // Once a haul route has been assigned to a delivery it should keep showing here even if the
    // haul route is later archived (e.g. a completed delivery's historical record shouldn't look
    // like its haul route reference disappeared). haulRoutesForProject only returns active routes,
    // so an archived-but-assigned route is fetched separately as a fallback.
    const [archivedAssignedHaulRoute, setArchivedAssignedHaulRoute] = React.useState<HaulRoute | null>(null);

    const selectedHaulRouteId = props.calendarEvent.deliveryHaulRouteId();
    const gateId = props.calendarEvent.deliveryGateId();

    const dropOffMapObject = useMemo(() => {
        if (!dropoff) return null;
        return dropoff.getMapObject(context);
    }, [context, dropoff, mapObjectsByProject]);

    const gate = useMemo(() => {
        if (!project) return null;
        return gatesForProject(project.id)?.find((gate: Gate) => gate.id === gateId) || null;
    }, [gatesForProject, project, gateId]);

    const gateMapObject = useMemo(() => {
        if (!gate) return null;
        return gate.getMapObject(context);
    }, [context, gate, mapObjectsByProject]);

    // The leg from the gate to the dropoff once inside the jobsite, which follows from the delivery's gate and dropoff.
    const gateToDropoffRoutes = useMemo(() => {
        if (!project) return [];
        return gateToDropoffRoutesForPair(
            gateToDropoffRoutesForProject(project.id),
            gate?.id ?? null,
            dropoff?.id ?? null
        );
    }, [gateToDropoffRoutesForProject, gateToDropoffRoutesByProject, project, gate, dropoff]);

    const gateToDropoffRouteMapObjects = useMemo(
        () =>
            gateToDropoffRoutes
                .map((route) => route.getMapObject(context))
                .filter((mapObject): mapObject is MapObject => mapObject !== null),
        [context, gateToDropoffRoutes, mapObjectsByProject]
    );

    const haulRoutes = useMemo(() => {
        if (!project) return [];
        return haulRoutesForProject(project.id);
    }, [haulRoutesForProject, haulRoutesByProject, project]);

    const selectedHaulRoute = useMemo(() => {
        return haulRoutes.find((haulRoute) => haulRoute.id === selectedHaulRouteId) || archivedAssignedHaulRoute;
    }, [haulRoutes, selectedHaulRouteId, archivedAssignedHaulRoute]);

    const selectedHaulRouteMapObject = useMemo(() => {
        if (!selectedHaulRoute) return null;
        return selectedHaulRoute.getMapObject(context);
    }, [context, selectedHaulRoute]);

    // Until a route is picked every option shows in its own color so they can be told apart; once one is
    // picked the alternates go grey and the pick is rendered last so it sits on top, like a Google Maps
    // route picker. The pick comes from selectedHaulRouteMapObject (not the active-only haulRoutes list)
    // so an archived-but-assigned route still draws on the map.
    const haulRouteMapObjects = useMemo(() => {
        const alternates: MapObject[] = haulRoutes
            .filter((haulRoute) => haulRoute.id !== selectedHaulRouteId)
            .map((haulRoute) => haulRoute.getMapObject(context))
            .filter((mapObject): mapObject is MapObject => mapObject !== null);

        if (!selectedHaulRouteMapObject) {
            return alternates;
        }

        const greyedAlternates = alternates.map((mapObject) => {
            const displayMapObject = Object.create(mapObject) as MapObject;
            displayMapObject.color = ALTERNATE_HAUL_ROUTE_COLOR;
            displayMapObject.outline = ALTERNATE_HAUL_ROUTE_COLOR;
            return displayMapObject;
        });

        return [...greyedAlternates, selectedHaulRouteMapObject];
    }, [context, haulRoutes, selectedHaulRouteId, selectedHaulRouteMapObject]);

    const mapObjects = useMemo(() => {
        return [dropOffMapObject, gateMapObject, ...haulRouteMapObjects, ...gateToDropoffRouteMapObjects].filter(
            (obj): obj is MapObject => obj !== null
        );
    }, [dropOffMapObject, gateMapObject, haulRouteMapObjects, gateToDropoffRouteMapObjects]);

    useEffect(() => {
        if (!project) {
            setDropoff(null);
            return;
        }

        const d = dropoffsForProject(project.id).find(
            (dropoff) => dropoff.id === props.calendarEvent.deliveryDropOffId()
        );

        setDropoff(d || null);
    }, [props.calendarEvent, dropoffsByProject, dropoffsForProject, project]);

    useEffect(() => {
        if (!project || !selectedHaulRouteId) {
            setArchivedAssignedHaulRoute(null);
            return;
        }

        if (haulRoutes.some((haulRoute) => haulRoute.id === selectedHaulRouteId)) {
            setArchivedAssignedHaulRoute(null);
            return;
        }

        if (archivedAssignedHaulRoute?.id === selectedHaulRouteId) {
            return;
        }

        PlotAPI.getProjectHaulRoute(
            project.claimed_by_team_subdomain || "projects",
            project.unique_token,
            selectedHaulRouteId,
            project.timezone
        ).then((haulRoute) => setArchivedAssignedHaulRoute(haulRoute));
    }, [project, selectedHaulRouteId, haulRoutes, archivedAssignedHaulRoute]);

    const handleMapDirections = () => {
        if (selectedHaulRouteMapObject) {
            setDamnDirectionsModal(true);
            return;
        }
        if (dropOffMapObject) {
            Helper.openMapLink(dropOffMapObject, gateMapObject ? [gateMapObject] : []);
        }
        return;
    };

    const selectHaulRoute = (haulRoute: HaulRoute) => {
        if (!project || haulRoute.id === selectedHaulRouteId) {
            return;
        }

        setSavingHaulRoute(true);

        const newCalendarEvent = Object.create(props.calendarEvent) as CalendarEvent;

        const newResources =
            newCalendarEvent.calendarEventResources?.map(
                (resource) => Object.create(resource) as CalendarEventResource
            ) || [];

        const existingHaulRouteResources = newResources.filter((resource) => resource.deliveryRole === "haul_route");

        if (existingHaulRouteResources.length > 0) {
            // Update the already-assigned resource in place rather than destroying it and creating a
            // new one - simpler, and avoids any doubt about destroy+create ordering within a single
            // nested-attributes update.
            existingHaulRouteResources[0].calendarResourceableId = haulRoute.id;

            // If more than one haul_route resource somehow already exists (e.g. left over from a past
            // bug), destroy the extras so we only ever end up with one.
            existingHaulRouteResources.slice(1).forEach((resource) => {
                if (resource.id) {
                    resource._destroy = true;
                }
            });
        } else {
            newResources.push(
                CalendarEventResource.fromApiObject(
                    {
                        calendar_resourceable_id: haulRoute.id,
                        calendar_resourceable_type: "HaulRoute",
                        consumes_availability: false,
                        delivery_role: "haul_route",
                        participation_status: "accepted",
                    },
                    project.timezone
                )
            );
        }

        newCalendarEvent.calendarEventResources = newResources;

        context
            .updateCalendarEvent(newCalendarEvent)
            .then((updated) => {
                if (!updated) {
                    throw new Error("Unable to update haul route");
                }

                props.setCalendarEvent(updated);
            })
            .catch(() => {
                notification.error({
                    message: "Unable to Update Haul Route",
                    description: "Please try again.",
                });
            })
            .finally(() => {
                setSavingHaulRoute(false);
            });
    };

    const handleMapObjectClick = (mapObject: MapObject) => {
        if (savingHaulRoute) return;

        const mappable = mapObject.getMappable(context);
        if (mappable instanceof HaulRoute) {
            selectHaulRoute(mappable);
        }
    };

    if (mapObjects.length === 0) {
        return null;
    }

    const verifyMapObjects = () => {
        if (!mapObjects || mapObjects.length === 0) {
            return false;
        }

        return true;
    };

    const deliveryDirectionsModalText = () => (
        <>
            <DialogContentText>This delivery has a preapproved haul route to follow to the jobsite.</DialogContentText>
            <DialogContentText>
                Click Get Directions below to navigate to the start of{" "}
                <div className="flex flex-row items-center font-semibold">
                    {selectedHaulRouteMapObject && (
                        <AreaAvatar mappable={selectedHaulRoute} mapObject={selectedHaulRouteMapObject} />
                    )}{" "}
                    {selectedHaulRoute?.name}
                </div>
                {gate && (
                    <>
                        and then enter the jobsite at{" "}
                        <div className="flex flex-row items-center font-semibold">
                            {gateMapObject && <AreaAvatar mappable={gate} mapObject={gateMapObject} />} {gate.name}
                        </div>
                    </>
                )}
                and then proceed to dropoff at{" "}
                <div className="flex flex-row items-center font-semibold">
                    <AreaAvatar mappable={dropoff} /> {dropoff?.name}
                </div>
            </DialogContentText>
        </>
    );

    return (
        <section className="mt-4 xl:mx-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200/50">
                <div className="p-5 pb-4 bg-linear-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex gap-2 items-center">
                            <FontAwesomeIcon icon={faMap} className="text-plot-orange" />
                            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Navigation</h2>
                        </div>

                        <button
                            type="button"
                            onClick={handleMapDirections}
                            className="cursor-pointer bg-linear-to-r from-plot-orange to-plot-orange/90 hover:from-plot-orange/90 hover:to-plot-orange text-white font-bold p-3 md:p-4 rounded-xl flex items-center justify-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <FontAwesomeIcon icon={faLocationArrow} />
                            Get Directions
                        </button>
                    </div>
                </div>

                {verifyMapObjects() && (
                    <div className="relative w-full h-80 bg-linear-to-br from-gray-100 to-gray-200">
                        <DamnMap
                            mapId="damnMap"
                            mapObjects={mapObjects}
                            geoControlRef={context.geoControlRef}
                            setUserLocation={context.setUserLocation}
                            project={project}
                            route={selectedHaulRoute}
                            stepRoutes={gateToDropoffRoutes}
                            onMapObjectClick={handleMapObjectClick}
                        />
                    </div>
                )}

                {/* Navigation Info & Button */}
                <div className="mt-5 flex flex-col gap-4 w-full">
                    <HaulRouteSection
                        haulRoutes={haulRoutes}
                        selectedHaulRoute={selectedHaulRoute}
                        selectedHaulRouteId={selectedHaulRouteId}
                        saving={savingHaulRoute}
                        onSelect={selectHaulRoute}
                    />
                    <GateSection gate={gate} gateMapObject={gateMapObject} />

                    <GateToDropoffRouteSection routes={gateToDropoffRoutes} dropoff={dropoff} />

                    <DropoffSection dropoff={dropoff} dropoffMapObject={dropOffMapObject} />
                </div>
            </div>
            <DamnDirectionsModal
                open={damnDirectionsModal}
                toggleOpen={() => setDamnDirectionsModal(!damnDirectionsModal)}
                handleMapDirections={() => {
                    const destination = dropOffMapObject || selectedHaulRouteMapObject;
                    if (!destination) return;

                    const waypoints = [selectedHaulRouteMapObject, gateMapObject].filter(
                        (mapObject): mapObject is MapObject => mapObject !== null && mapObject !== destination
                    );

                    Helper.openMapLink(destination, waypoints);
                }}
                body={deliveryDirectionsModalText()}
            />
        </section>
    );
};

export default DamnMapSection;
